/**
 * soleil-animations.js — GSAP motion layer (Soleil frontend stack)
 *
 * Owns ALL scroll-triggered / multi-step / entrance / counter motion, per the
 * frontend layering rules in CLAUDE.md ("Theme Architecture → Frontend layering"
 * + "Motion & Navigation → CSS vs GSAP"). Simple hover, ambient loops (marquee),
 * and reduced-motion fallbacks stay in CSS using the --soleil-motion-* tokens.
 *
 * Load contract (self-hosted, deferred, in snippets/scripts.liquid — shared by
 * theme.liquid + password.liquid). Order is load-bearing:
 *   gsap.min.js → scrolltrigger.min.js → alpine.min.js → soleil-animations.js
 * GSAP + ScrollTrigger are therefore plain globals by the time this runs.
 *
 * To add motion to a section: write a self-contained init function that no-ops
 * when its target is absent (guard with a querySelector check), then call it
 * from initSoleilMotion(). Never run a CSS animation AND GSAP on the same
 * element (CLAUDE.md hard rule).
 */
(function () {
  'use strict';

  if (typeof window.gsap === 'undefined') {
    console.warn('[soleil-animations] GSAP not found — motion layer disabled.');
    return;
  }

  // Capture our own <script> URL synchronously — document.currentScript is only
  // valid during top-level eval, not later inside the deferred init. The Motion
  // Lab bootstrap (SPEC-motion-lab.md A2) derives its sibling asset URLs
  // (gsdevtools.min.js, sl-motion-lab.js) from this, so no Liquid change to
  // snippets/scripts.liquid is needed and nothing about the Lab ships to visitors.
  var SOLEIL_ANIM_SRC = (document.currentScript && document.currentScript.src) || '';

  // Register ScrollTrigger + the free GSAP 3.13 plugins IF they loaded (DrawSVG
  // line-art, SplitText headline reveals — self-hosted in assets/, wired in
  // snippets/scripts.liquid). Pass only what's actually present so the layer
  // still works when a plugin is absent.
  gsap.registerPlugin.apply(gsap, [ScrollTrigger, window.DrawSVGPlugin, window.SplitText].filter(Boolean));

  // SOLEIL (2026-07-01): the Horizon 4.x desktop "squeeze" is RETIRED (see
  // assets/scroll-container.js SQUEEZE_ACTIVE = false) — the root document scrolls
  // natively at every width, which is also ScrollTrigger's default scroller, so no
  // explicit scroller is needed. The 990px listener is kept only to re-measure
  // trigger positions when the layout reflows across the breakpoint.
  var soleilSqueezeQuery = window.matchMedia('(min-width: 990px)');
  ScrollTrigger.defaults({ scroller: window });
  soleilSqueezeQuery.addEventListener('change', function () {
    ScrollTrigger.refresh();
  });

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Public flags so per-section recipes can gate heavy motion (e.g. skip
  // parallax on mobile) without re-reading the media queries each time.
  //
  // `debug` is the SINGLE source of truth for the Motion Lab (SPEC-motion-lab.md
  // Part A): flipped on only when the URL carries `?motion-debug`. It gates the
  // dev-only overlay + GSDevTools + conditional ScrollTrigger markers. Matched as
  // a real query param ([?&] before, delimiter/end after) so `?xmotion-debug`
  // can't false-trigger. `registry` is a Map of every timeline the engine
  // creates (see registerMotion); it is populated ALWAYS (negligible — a handful
  // of object references) and read only under the flag.
  window.SoleilMotion = {
    enabled: !prefersReducedMotion,
    prefersReducedMotion: prefersReducedMotion,
    isMobile: window.innerWidth < 768,
    debug: /[?&]motion-debug(?:=|&|$)/.test(window.location.search),
    registry: new Map()
  };

  // ── Timeline auto-registry (SPEC-motion-lab.md A1) ──────────────────────────
  // Every persistent tween/timeline the engine creates passes through here so the
  // Lab can list, badge, scrub (GSDevTools) and detach it. Ephemeral tweens
  // (per-hover lifts) are intentionally NOT registered. The helper is a
  // pass-through: it returns `anim` unchanged so call sites wrap inline —
  //   registerMotion(gsap.from(el, {...}), el, 'name')
  // Naming per spec: `[section-handle] · [preset-or-choreo-name]`, the handle
  // derived from the closest `#shopify-section-*` ancestor of the target element
  // (falls back to 'page' for section-less targets like #MainContent).
  //
  // meta (all optional):
  //   loop    — true for ambient yoyo loops; sets the `loop` badge + flag.
  //   trigger — an explicit ScrollTrigger (for the paused-loop pattern, where the
  //             gate is a separate ScrollTrigger.create, NOT attached to the tween).
  //   kind    — override the auto-derived badge ('scroll' | 'loop' | 'load').
  //   section — override the derived section handle.
  // kind auto-derives: loop → 'loop', else a ScrollTrigger present → 'scroll',
  // else → 'load'. (No engine timeline uses scrub:true yet; 'scrub' is reserved
  // for Part B recipes.)
  var _soleilMotionSeq = 0;
  function soleilSectionHandle(el) {
    if (!el || !el.closest) return 'page';
    var sec = el.closest('[id^="shopify-section-"]');
    return sec ? sec.id.replace('shopify-section-', '') : 'page';
  }
  function registerMotion(anim, el, name, meta) {
    if (!anim) return anim;
    meta = meta || {};
    var handle = meta.section || soleilSectionHandle(el);
    var trigger = meta.trigger || (anim && anim.scrollTrigger) || null;
    var kind = meta.kind || (meta.loop ? 'loop' : (trigger ? 'scroll' : 'load'));
    var id = ++_soleilMotionSeq;
    window.SoleilMotion.registry.set(id, {
      id: id,
      anim: anim,
      el: el || null,
      section: handle,
      label: name,
      name: handle + ' · ' + name,
      loop: !!meta.loop,
      kind: kind,
      trigger: trigger
    });
    return anim;
  }
  // Exposed on the existing global (no second global) so frozen Layer-2
  // choreographies + the Lab can register their own timelines too.
  window.SoleilMotion.registerMotion = registerMotion;

  // Drop an entry when a recipe is reverted — without this, every slider change
  // in the Lab (revert + re-apply) would leave a dead row behind in the panel.
  function unregisterMotion(anim) {
    var stale = null;
    window.SoleilMotion.registry.forEach(function (v, k) { if (v.anim === anim) stale = k; });
    if (stale !== null) window.SoleilMotion.registry['delete'](stale);
  }

  // ── Conditional ScrollTrigger markers (SPEC-motion-lab.md A3) ────────────────
  // Every engine ScrollTrigger config passes through stCfg(), so `?motion-debug`
  // turns GSAP's start/end markers on everywhere from ONE source of truth
  // (window.SoleilMotion.debug — same flag that boots the Lab). Off-flag it hands
  // the config back untouched: no extra property, no cost, no visual trace.
  // The Lab layers a global show/hide on top (html.sl-lab-nomarkers) so the
  // markers can be silenced without a reload — markers cannot be toggled on a
  // ScrollTrigger after creation, hence the CSS-level switch.
  function stCfg(cfg) {
    if (window.SoleilMotion.debug) cfg.markers = true;
    return cfg;
  }

  /**
   * Product-card entrance — staggered fade + rise as each product grid scrolls
   * into view. First real recipe; proves the pipeline end-to-end (ScrollTrigger
   * + the window.SoleilMotion reduced-motion gate).
   *
   * Grid container selector — TWO classes, on purpose:
   *   `.resource-list--grid`  → the LIVE Horizon 4.1.1 collection/featured grid
   *                             (BEM modifier, `display:grid`). This is what
   *                             actually renders today.
   *   `.product-grid`         → the Horizon 3.x class, gone since the 4.1.1 merge.
   *                             Kept purely for back-compat; matches nothing now.
   * The 4.1.1 rename silently killed this recipe on collection pages (`.product-grid`
   * resolved to 0 — surfaced by the Motion Lab registry coming up empty on
   * /collections/all). Cards are `.product-card` regardless of grid version.
   *
   * Deliberately NOT matched: the homepage featured carousel is
   * `.resource-list.resource-list__carousel` — `__carousel` is a BEM *element*
   * (double underscore), so a `--grid` *modifier* selector cannot match it. That
   * keeps the stagger entrance off carousels (they own their own slideshow motion)
   * with zero double-fire risk. One ScrollTrigger per grid so every grid on a page
   * staggers its own cards independently.
   *
   * Degradation: if GSAP fails to load, this never runs and cards render at
   * their natural (visible) state — no inline opacity:0 is left behind.
   */
  function initProductCardEntrance() {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    gsap.utils.toArray('.product-grid, .resource-list--grid').forEach(function (grid) {
      var cards = grid.querySelectorAll('.product-card');
      if (!cards.length || grid.dataset.soleilEntranceBound) return;
      grid.dataset.soleilEntranceBound = 'true';
      // Skip the entrance for any grid already intersecting the initial viewport.
      // The server HTML paints cards at opacity 1; because GSAP is deferred, gsap.from's
      // immediateRender snaps those already-visible cards to opacity 0 ~250ms after paint
      // and fades them back — a visible "blink then load" on above-the-fold grids (the
      // reported collections-page flash; runtime-confirmed 2026-07-06). Content in view on
      // load must simply stay visible; the entrance is only for grids scrolled into view.
      var rect = grid.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) return;
      // Observe actual visibility across both .page-wrapper (desktop) and
      // document scrolling (mobile). Never pre-hide cards awaiting a trigger.
      if (!('IntersectionObserver' in window)) return;
      var observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        observer.disconnect();
        document.removeEventListener('shopify:section:unload', cleanup);
        if (!grid.isConnected || !window.SoleilMotion.enabled) return;
        registerMotion(gsap.from(cards, {
          y: 24,
          opacity: 0,
          duration: 0.6,
          stagger: 0.08,
          ease: 'power2.out',
          clearProps: 'transform,opacity'
        }), grid, 'product-card-entrance', { kind: 'load' });
      }, { threshold: 0 });
      function cleanup(event) {
        if (!event.target.contains(grid)) return;
        observer.disconnect();
        document.removeEventListener('shopify:section:unload', cleanup);
      }
      document.addEventListener('shopify:section:unload', cleanup);
      observer.observe(grid);
    });
  }

  /**
   * Product-card hover lift — GSAP, per the frontend-stack rule that ALL motion
   * lives in the motion layer (not CSS). Lifts the card FACE (.product-card__content,
   * which carries the brand border + signature shadow) on hover; the GSAP entrance
   * above animates the PARENT (.product-card), so the two never animate the same
   * element (CLAUDE.md hard rule). Idempotent: a data-flag prevents double-binding
   * when re-scanned after a Theme Editor section reload. The lift moves outside the
   * card box — soleil-custom.css keeps .product-card overflow:visible so it isn't
   * cropped (owner-flagged concern).
   */
  function initProductCardHover() {
    if (!window.SoleilMotion.enabled) return;
    gsap.utils.toArray('.product-card').forEach(function (card) {
      if (card.dataset.grvHoverBound) return;
      var face = card.querySelector('.product-card__content');
      if (!face) return;
      card.dataset.grvHoverBound = 'true';
      // Crisp translate-only lift. The earlier tilt-lift (rotation:-0.6 + scale:1.01)
      // promoted the FACE to a GPU layer whose cached bitmap was then resampled by the
      // sub-degree rotation + fractional scale → a persistent slight blur across the
      // card text/ink-border/price the whole time it was hovered (V2 bug 1.2 — the
      // "card blurs when quick-add appears" report; native card_hover_effect is `none`,
      // so this tween was the sole source). translateY does NOT resample the layer, so
      // a pure y-lift keeps the card pin-sharp while preserving the sticker-peel cue.
      card.addEventListener('mouseenter', function () {
        gsap.to(face, { y: -6, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(face, { y: 0, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
      });
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Motion system — brand-neutral engine; recipes below are structural plumbing.
  // ONLY the net-new engines that use NEW selectors are ported — the CSS class
  // palette (.soleil-fade-up / -wipe / -blur-in / -scale-soft / -slide-in /
  // -typewriter) stays CSS + .is-visible (soleil-wipe.js IntersectionObserver);
  // GSAP must NOT also drive those (CLAUDE.md: two systems on one element = bug).
  // ───────────────────────────────────────────────────────────────────────────

  // Scroll-enter trigger: fire as the element's top reaches ~90% down the
  // viewport. once:true == single-fire (no replay on scroll-back).
  var START = 'top 90%';
  // Marks an element whose tween was created, so the initial pass + section:load
  // rescans never double-bind the same node.
  var DONE = 'data-soleil-anim-done';
  function bind(el) {
    if (el.hasAttribute(DONE)) return false;
    el.setAttribute(DONE, '');
    return true;
  }

  // Movimento — the settings-driven preset engine. One initializer reads every
  // [data-soleil-anim] (emitted by snippets/soleil-motion-attrs.liquid from the
  // shared "Movimento" block settings) and applies the GSAP preset with the
  // merchant's knobs. Entrances (mode in) fire once then clearProps; the three
  // ambient presets (mode loop) run viewport-gated + mobile-lightened. Transform/
  // opacity only, except blur-in (filter, mobile-capped). reduced-motion → no tween.
  function initMotionPresets(root) {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('[data-soleil-anim]').forEach(function (el) {
      var p = el.getAttribute('data-soleil-anim');
      if (!p || p === 'none') return;
      // 'stepped' (spec 2.22B): CSS drives the stepped stop-motion loop; GSAP must
      // not also tween it. JS only publishes the merchant knobs (duration / step
      // count / amplitude / direction) as CSS custom props via setProperty — which
      // MERGES into the element's inline style, so it works even on hosts that
      // already carry a `style` attribute (image/text/svg), where a second `style`
      // attribute from Liquid would be dropped. Runs under reduced-motion too (the
      // vars are inert; the CSS media query disables the animation).
      if (p === 'stepped') {
        if (!bind(el)) return;
        var sDur = parseFloat(el.getAttribute('data-soleil-anim-dur')) || 1.4;
        var sCount = parseInt(el.getAttribute('data-soleil-anim-steps'), 10) || 12;
        var sAmt = parseFloat(el.getAttribute('data-soleil-anim-amt'));
        if (isNaN(sAmt)) sAmt = 24;
        var sAxis = el.getAttribute('data-soleil-anim-axis') || 'up';
        var sx = 0, sy = -sAmt;
        if (sAxis === 'down') { sy = sAmt; }
        else if (sAxis === 'left') { sy = 0; sx = -sAmt; }
        else if (sAxis === 'right') { sy = 0; sx = sAmt; }
        el.style.setProperty('--soleil-step-dur', sDur + 's');
        el.style.setProperty('--soleil-step-count', String(sCount));
        el.style.setProperty('--soleil-step-x', sx + 'px');
        el.style.setProperty('--soleil-step-y', sy + 'px');
        return;
      }
      if (!bind(el)) return;

      if (prefersReducedMotion) { gsap.set(el, { opacity: 1 }); return; }

      var mode   = el.getAttribute('data-soleil-anim-mode') || 'in';
      var dur    = parseFloat(el.getAttribute('data-soleil-anim-dur')) || 0.6;
      var delay  = parseFloat(el.getAttribute('data-soleil-anim-delay')) || 0;
      var bounce = el.getAttribute('data-soleil-anim-ease') !== 'smooth';
      var ease   = bounce ? 'back.out(1.6)' : 'power3.out';
      var amt    = parseFloat(el.getAttribute('data-soleil-anim-amt'));
      if (isNaN(amt)) amt = 24;

      if (mode === 'loop') {
        // Ambient loops honor anim_dur + anim_amt + anim_axis (spec 2.22B). durFactor
        // is 1.0 at the schema default (0.6s) so the tuned default durations are
        // unchanged; the amplitude divisors map anim_amt's default (24) back to each
        // preset's original amplitude → defaults render identically, only merchant
        // edits change them. Additive.
        var f = isMobile ? 0.5 : 1, tween;
        var durFactor = dur / 0.6;
        var laxis = el.getAttribute('data-soleil-anim-axis') || 'up';
        if (p === 'float') {
          var fa = (amt / 3) * f;                 // default 24 → 8 (unchanged)
          var fv = { duration: 2.4 * durFactor, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true };
          if (laxis === 'left')       fv.x = -fa;
          else if (laxis === 'right') fv.x = fa;
          else if (laxis === 'down')  fv.y = fa;
          else                        fv.y = -fa; // up
          tween = gsap.to(el, fv);
        } else if (p === 'drift') {
          tween = gsap.to(el, { x: (amt / 4) * f, y: -(amt / 6) * f, rotation: 1.5 * f, duration: 3.6 * durFactor, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true });
        } else {
          tween = gsap.to(el, { rotation: (amt / 9.6) * f, duration: 2.8 * durFactor, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true }); // sway
        }
        var loopGate = ScrollTrigger.create(stCfg({
          trigger: el, start: 'top bottom', end: 'bottom top',
          onToggle: function (self) { self.isActive ? tween.resume() : tween.pause(); }
        }));
        registerMotion(tween, el, p, { loop: true, trigger: loopGate });
        return;
      }

      // entrance presets
      var from = { opacity: 0 }, extra = {};
      if (p === 'fade-up')         from.y = amt;
      else if (p === 'slide-in')   from.x = -amt;
      else if (p === 'scale-soft') from.scale = 0.92;
      else if (p === 'pop')      { from.scale = 0.6; ease = bounce ? 'back.out(2.4)' : 'power3.out'; }
      else if (p === 'carimbo')  { from.scale = 0.9; from.rotation = -6; from.y = 14; ease = bounce ? 'back.out(1.7)' : 'power3.out'; }
      else if (p === 'blur-in')  { from.filter = 'blur(' + (isMobile ? 6 : 12) + 'px)'; extra.filter = 'blur(0px)'; }
      else if (p === 'wipe')     { from.opacity = 1; from.clipPath = 'inset(0 100% 0 0)'; extra.clipPath = 'inset(0 0% 0 0)'; }

      el.style.willChange = (p === 'blur-in') ? 'filter, opacity' : 'transform, opacity';
      var to = Object.assign({
        opacity: 1, x: 0, y: 0, scale: 1, rotation: 0,
        duration: dur, delay: delay, ease: ease,
        clearProps: 'transform,filter,clipPath,willChange'
      }, extra);
      if (el.getAttribute('data-soleil-anim-trigger') !== 'load') {
        to.scrollTrigger = stCfg({ trigger: el, start: START, once: true });
      }
      registerMotion(gsap.fromTo(el, from, to), el, p);
    });
  }

  // Pattern drift (soleil-pattern block) — ambient GPU transform on the oversized
  // .soleil-pattern__layer (clipped by the shell's overflow, so the drift never
  // reveals an edge). Mobile-inclusive but lightened. Viewport-gated. RM → none.
  function initPatternDrift(root) {
    if (prefersReducedMotion) return;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('[data-soleil-pattern-motion="drift"]').forEach(function (host) {
      var layer = host.querySelector('.soleil-pattern__layer');
      if (!layer) return;
      if (!bind(host)) return;
      var amp = isMobile ? 1.5 : 3;
      var dur = isMobile ? 14 : 9;
      var tween = gsap.to(layer, {
        xPercent: amp, yPercent: -amp, duration: dur,
        ease: 'sine.inOut', repeat: -1, yoyo: true, paused: true
      });
      var driftGate = ScrollTrigger.create(stCfg({
        trigger: host, start: 'top bottom', end: 'bottom top',
        onToggle: function (self) { self.isActive ? tween.play() : tween.pause(); }
      }));
      registerMotion(tween, host, 'pattern-drift', { loop: true, trigger: driftGate });
    });
  }

  // SplitText headline reveal. Opt-in via .soleil-headline (Custom CSS class hook).
  // Split AFTER document.fonts.ready (self-hosted brand fonts — splitting against
  // fallback metrics would mis-wrap, then reflow on swap). SplitText absent → reveal
  // without splitting. Hidden initial state + RM reveal live in soleil-custom.css.
  function animateHeadlines(root) {
    if (prefersReducedMotion) return;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('.soleil-headline').forEach(function (el) {
      if (!bind(el)) return;
      if (!window.SplitText) { gsap.set(el, { autoAlpha: 1 }); return; }
      var target = el.querySelector('h1, h2, h3, h4, h5, h6, p') || el;
      var split = new SplitText(target, { type: 'words', wordsClass: 'soleil-headline__word' });
      gsap.set(el, { autoAlpha: 1 });
      registerMotion(gsap.from(split.words, {
        yPercent: isMobile ? 60 : 100,
        opacity: 0,
        duration: isMobile ? 0.5 : 0.7,
        ease: 'power3.out',
        stagger: isMobile ? 0.03 : 0.05,
        scrollTrigger: stCfg({ trigger: el, start: START, once: true })
      }), el, 'headline');
    });
  }

  // DrawSVG line-art draw-on. Opt-in via .soleil-draw wrapping inline STROKED SVG
  // (the natural carrier is the soleil-svg block: class_name "soleil-draw" + a
  // line-art SVG). Each shape self-draws on scroll-enter, once, staggered. Plugin
  // absent / RM → art left fully drawn (no-op).
  function initDrawSVG(root) {
    if (prefersReducedMotion || !window.DrawSVGPlugin) return;
    root.querySelectorAll('.soleil-draw').forEach(function (host) {
      if (!bind(host)) return;
      var strokes = host.querySelectorAll('path, line, polyline, polygon, circle, rect, ellipse');
      if (!strokes.length) return;
      gsap.set(strokes, { drawSVG: 0 });
      registerMotion(gsap.to(strokes, {
        drawSVG: '100%', duration: 1.1, ease: 'power2.out', stagger: 0.15,
        scrollTrigger: stCfg({ trigger: host, start: START, once: true })
      }), host, 'draw');
    });
  }

  // Curtain reveal — a section-entrance clip un-clipping from the bottom up, once.
  // Opt-in via .soleil-curtain (Custom CSS class hook). Initial clipped state + RM
  // reveal live in soleil-custom.css. Mobile-inclusive (cheap GPU clip+transform).
  function animateCurtains(root) {
    if (prefersReducedMotion) return;
    root.querySelectorAll('.soleil-curtain').forEach(function (el) {
      if (!bind(el)) return;
      registerMotion(gsap.fromTo(el,
        { clipPath: 'inset(100% 0 0 0)', y: 24 },
        {
          clipPath: 'inset(0% 0 0 0)', y: 0, duration: 0.9, ease: 'power3.out',
          scrollTrigger: stCfg({ trigger: el, start: START, once: true })
        }
      ), el, 'curtain');
    });
  }

  // Headlines wait on fonts; everything else runs immediately.
  function whenFontsReady(cb) {
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(cb); }
    else { cb(); }
  }

  // Global section scroll-reveal (spec 2.5). Opt-in site-wide via the "Globais — by
  // studiolab" setting → `.soleil-reveal-sections` on <body>. Every content section
  // after the first (the first is the LCP/above-fold candidate — never hidden) fades +
  // rises as it enters the viewport, once. The initial hidden state is a
  // reduced-motion-gated CSS opacity pre-hide (soleil-custom.css) — opacity + transform
  // only, so there is NO layout shift (CLS). This function must run even when motion is
  // disabled (mobile/RM), because the CSS may still have pre-hidden the sections — in
  // that case it reveals them instantly. Sections that carry explicit Movimento attrs
  // are left to initMotionPresets. Only clears `transform` on complete so the inline
  // opacity:1 keeps beating the CSS pre-hide.
  function initSectionReveal(root) {
    if (!document.body.classList.contains('soleil-reveal-sections')) return;
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll('#MainContent > .shopify-section ~ .shopify-section');
    els.forEach(function (el) {
      if (el.hasAttribute('data-soleil-anim')) return;
      if (!bind(el)) return;
      var animate = window.SoleilMotion.enabled && !prefersReducedMotion && window.ScrollTrigger;
      if (!animate) { gsap.set(el, { opacity: 1, y: 0 }); return; }
      el.style.willChange = 'transform, opacity';
      registerMotion(gsap.fromTo(el,
        { opacity: 0, y: 24 },
        {
          opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
          clearProps: 'transform,willChange',
          scrollTrigger: stCfg({ trigger: el, start: 'top 90%', once: true })
        }), el, 'section-reveal');
    });
  }

  // Page transition (spec 2.6). Entrance-only, GSAP, opt-in via "Transição de página"
  // (Globais) → `.soleil-page-transition` on <body>. On load the page content
  // (#MainContent) fades + rises in, softening the abrupt native full-page swap.
  // DELIBERATELY no link/click interception: native navigation proceeds normally, so
  // analytics/pixel fire exactly as before and navigation can never be delayed or
  // broken by JS — the highest risks the spec flagged. Runs once per page load. The
  // initial hidden state is a reduced-motion-gated opacity pre-hide (soleil-custom.css);
  // transform+opacity only → no CLS. Off by default (it trades a little LCP for the
  // reveal — enable + measure before shipping). Reveals instantly under RM/disabled.
  function initPageTransition() {
    if (!document.body.classList.contains('soleil-page-transition')) return;
    var main = document.getElementById('MainContent');
    if (!main || !bind(main)) return;
    if (!window.SoleilMotion.enabled || prefersReducedMotion) { gsap.set(main, { opacity: 1, y: 0 }); return; }
    main.style.willChange = 'transform, opacity';
    registerMotion(gsap.fromTo(main,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'transform,willChange' }),
      main, 'page-transition');
  }

  // ── SLMotion — host adapter for the portable Lab (SPEC-motion-lab.md C1) ────
  // The Lab (assets/sl-motion-lab.js) and the recipe modules
  // (assets/sl-motion-recipes.js) are studiolab-library property: they must run
  // in ANY Horizon theme. So they read ONE contract — window.SLMotion — and never
  // reach into this project's globals, selectors or tokens directly. Porting the
  // Lab to a new theme means copying those two files and rewriting this object.
  //
  // Everything below is either a pass-through to machinery this file already owns
  // or a value only the host can know (brand palette, which elements are already
  // animated here, the project's overlay z-index).
  window.SLMotion = {
    contract: 1,
    gsap: window.gsap,
    ScrollTrigger: window.ScrollTrigger,

    // Host state the Lab reads
    debug: window.SoleilMotion.debug,
    zIndex: 10000,                       // repo z-index table: custom overlays
    registry: window.SoleilMotion.registry,
    recipes: {},                         // filled by sl-motion-recipes.js

    // Brand palette offered by `token` params — the ONLY place brand colour
    // values enter the Lab. The starter ships this EMPTY (structural plumbing
    // only, no brand hex). Per project, fill it with the brand's named tokens,
    // e.g. [['name', '#RRGGBB'], ['accent', '#RRGGBB'], …]. Nothing else changes.
    tokens: [],

    // Elements this project already animates. A recipe that also drove one would
    // put two systems on a single element (CLAUDE.md hard rule) — the Movimento
    // engine tweens y/scale on [data-soleil-anim] and finishes with
    // clearProps:'transform', which would wipe a parallax matrix mid-scroll; the
    // CSS palette classes animate transform from a stylesheet.
    conflictSelector: '[data-soleil-anim]:not([data-soleil-anim="none"]),' +
                      '.soleil-fade-up,.soleil-slide-in,.soleil-scale-soft,.soleil-blur-in,.soleil-wipe',

    reducedMotion: prefersReducedMotion,
    registerMotion: registerMotion,
    unregisterMotion: unregisterMotion,
    stCfg: stCfg,
    refresh: function () { try { ScrollTrigger.refresh(); } catch (e) {} },

    // Kill a timeline AND its trigger AND its registry row in one call, so every
    // recipe cleanup() is a one-liner and cannot half-tear-down.
    killTimeline: function (tl) {
      if (!tl) return;
      try { tl.scrollTrigger && tl.scrollTrigger.kill(); } catch (e) {}
      try { tl.kill(); } catch (e) {}
      unregisterMotion(tl);
    },

    // B0 registration. Recipes self-describe; the panel reads the manifest and
    // generates its own controls, so adding a recipe never touches the Lab.
    defineRecipe: function (manifest) {
      if (!manifest || !manifest.name || typeof manifest.apply !== 'function') {
        console.warn('[sl-motion] invalid recipe manifest', manifest);
        return;
      }
      window.SLMotion.recipes[manifest.name] = manifest;
    },

    /**
     * Instantiate a recipe. ONE entry point for both callers — the Lab while
     * tuning, and a frozen Layer-2 choreography on load — so what ships is
     * exactly what was tuned.
     * Returns { timelines, refused, cleanup } or null.
     */
    applyRecipe: function (name, sec, params) {
      var m = window.SLMotion.recipes[name];
      if (!m || !sec) return null;
      if (prefersReducedMotion) return null;   // reduced motion = static state
      try {
        var inst = m.apply(sec, params || {}) || {};
        return {
          timelines: inst.timelines || [],
          refused: inst.refused || [],
          cleanup: inst.cleanup || function () {}
        };
      } catch (e) {
        console.warn('[sl-motion] recipe "' + name + '" failed', e);
        return null;
      }
    },

    /**
     * Resolve a frozen choreography's section key to its element.
     * Shopify's real DOM id is `template--<numeric-template-id>__<key>` and the
     * numeric half changes per theme/template copy, so a frozen config must NEVER
     * pin the full id. Matching the stable `__<key>` suffix is the convention
     * already used elsewhere in the repo (`[id$="soleil_footer"]` in
     * soleil-custom.css). A bare `shopify-section-<key>` is matched too, since
     * sections outside a JSON template are not template-prefixed.
     */
    sectionByKey: function (key) {
      if (!key) return null;
      var el = null;
      try {
        // SCOPED to section roots on purpose. An unscoped `[id$="__key"]` also
        // matches block-level ids that exist on real pages right now — e.g.
        // `QuickAdd-ProductForm-…__product_card_gallery_RcHyEF` — and block keys
        // come from the same generator as section keys, so a collision would
        // silently apply a choreography to a quick-add form instead of a section.
        el = document.querySelector('[id^="shopify-section-"][id$="__' + key + '"]');
      } catch (e) {
        // A key that is not selector-safe would throw SyntaxError out of
        // querySelector, up through pageChoreographies() and into init — killing
        // the ENTIRE motion layer over one bad string. Contain it here and let
        // the getElementById path (which cannot throw) still have its turn.
        //
        // Warn only under the flag: production must stay silent about a config
        // that fails to resolve, whatever the cause. The developer still sees it,
        // because a key that throws also resolves to null, so the Lab lists it
        // among the unresolved choreographies.
        if (window.SoleilMotion.debug) {
          console.warn('[sl-motion] section key is not selector-safe: "' + key + '"', e);
        }
      }
      // Sections outside a JSON template carry no `template--…__` prefix.
      return el || document.getElementById('shopify-section-' + key);
    },

    /**
     * Frozen choreographies that will NOT run on this page, with the reason.
     * Production ignores them silently by design; this exists so the Lab can say
     * so out loud, because a typo'd key is otherwise indistinguishable from a
     * config that is simply on another page.
     */
    unresolvedChoreographies: function () {
      return SOLEIL_CHOREOGRAPHIES.map(function (c) {
        if (!window.SLMotion.sectionByKey(c.sectionKey)) {
          return { config: c, reason: 'section-not-on-page' };
        }
        if (!window.SLMotion.recipes[c.recipe]) {
          return { config: c, reason: 'recipe-not-registered' };
        }
        return null;
      }).filter(Boolean);
    }
  };

  // Spec B0 names `window.SoleilMotion.recipes` as the registry. Same object,
  // two names: the host keeps its documented alias, the portable files use the
  // neutral contract. One reference — never a copy that could drift.
  window.SoleilMotion.recipes = window.SLMotion.recipes;
  window.SoleilMotion.applyRecipe = function (n, s, p) { return window.SLMotion.applyRecipe(n, s, p); };
  window.SoleilMotion.sectionByKey = function (k) { return window.SLMotion.sectionByKey(k); };

  // ── Frozen Layer-2 choreographies ───────────────────────────────────────────
  // Configs exported from the Lab and frozen here. Each is a plain object — the
  // recipe name, the STABLE section key, and the tuned params — applied on load.
  // Empty today: nothing has been frozen yet, which is why sl-motion-recipes.js
  // is not fetched on a normal page load.
  var SOLEIL_CHOREOGRAPHIES = [
    // { recipe: 'color-morph', sectionKey: 'section_mVCXDU', params: { ... } }
  ];

  // Exposed AFTER the array exists — the adapter object above is evaluated first,
  // so referencing it inside that literal would capture `undefined`. Same array
  // by reference, so the Lab always reads the current list.
  window.SLMotion.choreographies = SOLEIL_CHOREOGRAPHIES;

  // Scan freshly-added/ present elements for the ported engines. Safe to re-run
  // (the DONE attr dedupes). Excludes the existing product-card recipes, which
  // have their own guards + lifecycle below.
  function scanPortedMotion(root) {
    initMotionPresets(root);
    initPatternDrift(root);
    initDrawSVG(root);
    animateCurtains(root);
    initSectionReveal(root);
    whenFontsReady(function () { animateHeadlines(root); ScrollTrigger.refresh(); });
  }

  // Number count-up (theme task 6, Counter block). Animates [data-soleil-counter]
  // from start → end on scroll-into-view (once). Reduced-motion / disabled →
  // renders the final value immediately. pt-BR number formatting + prefix/suffix.
  function initCounters(root) {
    var els = (root || document).querySelectorAll('[data-soleil-counter]');
    for (var i = 0; i < els.length; i++) {
      (function (el) {
        if (el.dataset.soleilCounterBound) return;
        el.dataset.soleilCounterBound = '1';
        var end = parseFloat(el.dataset.soleilCounterEnd) || 0;
        var start = parseFloat(el.dataset.soleilCounterStart) || 0;
        var dur = parseFloat(el.dataset.soleilCounterDuration) || 2;
        var decimals = parseInt(el.dataset.soleilCounterDecimals || '0', 10) || 0;
        var prefix = el.dataset.soleilCounterPrefix || '';
        var suffix = el.dataset.soleilCounterSuffix || '';
        function fmt(v) {
          return prefix + Number(v).toLocaleString('pt-BR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
          }) + suffix;
        }
        var animate = window.SoleilMotion.enabled && !prefersReducedMotion && typeof gsap !== 'undefined';
        if (!animate) { el.textContent = fmt(end); return; }
        el.textContent = fmt(start);
        var io = new IntersectionObserver(function (entries) {
          for (var j = 0; j < entries.length; j++) {
            if (!entries[j].isIntersecting) continue;
            io.unobserve(el);
            var obj = { v: start };
            registerMotion(gsap.to(obj, {
              v: end, duration: dur, ease: 'power1.out',
              onUpdate: function () { el.textContent = fmt(obj.v); }
            }), el, 'counter', { kind: 'load' });
          }
        }, { threshold: 0.4 });
        io.observe(el);
      })(els[i]);
    }
  }

  function initSoleilMotion() {
    initCounters(document); // handles reduced-motion / disabled internally
    if (!window.SoleilMotion.enabled) {
      // Even with reduced motion, the preset engine must reveal hidden initial
      // states (it sets opacity:1 internally); the palette CSS handles its own RM.
      initMotionPresets(document);
      initSectionReveal(document); // reveal any CSS-pre-hidden sections instantly
      initPageTransition();        // reveal #MainContent instantly if it was pre-hidden
      return;
    }
    initProductCardEntrance();
    initProductCardHover();
    initPageTransition(); // once per page load (not on section:load)
    scanPortedMotion(document);
  }

  // Defer the motion setup until AFTER the browser's first paint (perf, 2026-07-01).
  // initSoleilMotion() creates every ScrollTrigger and runs an initial refresh —
  // `_refreshAll` measures the whole document, forcing a synchronous reflow. Running
  // it at DOMContentLoaded blocks the main thread inside the critical rendering window,
  // so the LCP hero can't paint until the thrash finishes (measured ~1.5s of "render
  // delay" on throttled mobile). A double-rAF fires right after the next paint, so the
  // hero paints first and GSAP inits ~one frame later — imperceptible for entrance
  // reveals (they're post-load by nature), but it takes the reflow off the LCP path.
  // ── Motion Lab bootstrap (SPEC-motion-lab.md A2) ────────────────────────────
  // Flag-gated: nothing here runs (nor is any byte fetched) unless the URL carries
  // ?motion-debug. Derives the assets/ base from our own <script src>, loads
  // GSDevTools (the scrubber; assets/gsdevtools.min.js) then the Lab overlay
  // (assets/sl-motion-lab.js) — both via classic <script> injection rather
  // than ES import(): Shopify serves assets cross-origin from cdn.shopify.com,
  // where dynamic import() would trip CORS/MIME, but a classic src load (how every
  // other Soleil asset loads) does not. See DECISIONS.md "Motion Lab".
  function soleilAssetBase() {
    var src = SOLEIL_ANIM_SRC;
    if (!src) {
      var s = document.querySelector('script[src*="soleil-animations.js"]');
      src = s ? s.src : '';
    }
    return src.replace(/soleil-animations\.js(?:\?.*)?$/, '');
  }
  function soleilLoadScript(url) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.onload = function () { resolve(url); };
      s.onerror = function () { reject(new Error('failed to load ' + url)); };
      document.head.appendChild(s);
    });
  }
  var _motionLabBooted = false;
  function bootMotionLab() {
    if (_motionLabBooted) return;
    _motionLabBooted = true;
    var base = soleilAssetBase();
    if (!base) { console.warn('[sl-motion-lab] could not resolve asset base — Lab unavailable.'); return; }
    soleilLoadScript(base + 'gsdevtools.min.js')
      .then(function () {
        // A load that resolves but leaves no global means the file was served but
        // did not evaluate (wrong MIME, a stray HTML 404 body, a GSAP-core version
        // it refuses). Record WHY — the Lab surfaces it as a persistent banner
        // instead of the old 1.2s row flash, which was easy to miss on a tablet.
        if (window.GSDevTools) { try { gsap.registerPlugin(window.GSDevTools); } catch (e) {} }
        else { window.SLMotion.gsdError = 'gsdevtools.min.js carregou mas não registrou o global GSDevTools.'; }
      })
      .catch(function () {
        window.SLMotion.gsdError = 'assets/gsdevtools.min.js não carregou (404 / bloqueio de rede).';
        console.warn('[sl-motion-lab] GSDevTools not loaded (assets/gsdevtools.min.js) — scrubber disabled; panel still works.');
      })
      .then(function () { return soleilLoadScript(base + 'sl-motion-lab.js'); })
      .catch(function (e) { console.warn('[sl-motion-lab] failed to load Lab module.', e); });
  }

  // ── Recipe modules, on demand (SPEC-motion-lab.md B0.1) ─────────────────────
  // assets/sl-motion-recipes.js SHIPS (a frozen choreography calls the same
  // apply() for visitors) but is fetched only when THIS page actually needs it.
  //
  // The gate is per-PAGE, not per-site: it asks whether a frozen choreography's
  // section is present in this document, never merely whether any choreography
  // exists in the array. Gating on the array alone would mean the first frozen
  // choreography starts pulling the module on every page of the store — cart,
  // policies, search — for motion those pages do not have. That is exactly the
  // budget line B0.1 draws ("a page with no choreography loads nothing beyond
  // core + ScrollTrigger"), and it is cheap: the check is one querySelector per
  // frozen entry, run once, after first paint.
  function pageChoreographies() {
    return SOLEIL_CHOREOGRAPHIES.filter(function (c) {
      return !!window.SLMotion.sectionByKey(c.sectionKey);
    });
  }

  var _recipesPromise = null;
  function soleilLoadRecipes() {
    if (_recipesPromise) return _recipesPromise;
    var base = soleilAssetBase();
    if (!base) return (_recipesPromise = Promise.resolve());
    _recipesPromise = soleilLoadScript(base + 'sl-motion-recipes.js')
      .catch(function (e) { console.warn('[sl-motion] recipe modules failed to load.', e); });
    return _recipesPromise;
  }

  // Apply the frozen Layer-2 choreographies present on this page. Runs AFTER the
  // recipe modules resolve, so it is async by nature — harmless for scroll-
  // scrubbed recipes (below the fold by definition), and refresh() re-measures
  // once at the end rather than per choreography.
  function applyFrozenChoreographies(list) {
    if (!list.length) return;
    list.forEach(function (c) {
      var sec = window.SLMotion.sectionByKey(c.sectionKey);
      if (!sec) return;   // raced away since the gate — treat as absent, not an error
      window.SLMotion.applyRecipe(c.recipe, sec, c.params);
    });
    window.SLMotion.refresh();
  }

  function scheduleSoleilMotionInit() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        initSoleilMotion();
        var mine = pageChoreographies();
        if (!window.SoleilMotion.debug && !mine.length) return;
        // Recipes must resolve BEFORE the Lab mounts — the panel builds its
        // controls from the manifests, so an empty registry would render an
        // empty recipe picker.
        soleilLoadRecipes().then(function () {
          applyFrozenChoreographies(mine);
          if (window.SoleilMotion.debug) bootMotionLab();
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSoleilMotionInit);
  } else {
    scheduleSoleilMotionInit();
  }

  // Recalculate trigger positions after late-loading content (images, fonts)
  // and after Theme Editor section re-renders in design mode.
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:load', function () {
    ScrollTrigger.refresh();
    if (window.SoleilMotion.enabled) initProductCardEntrance();
    initProductCardHover(); // bind hover on cards added by the editor (data-flag guards rebind)
    initCounters(document); // bind counters added by the editor (data-flag guards rebind)
    if (window.SoleilMotion.enabled) scanPortedMotion(document);
    else initMotionPresets(document);
  });
  document.addEventListener('shopify:section:unload', function () { ScrollTrigger.refresh(); });
})();
